import type {
	FilterConfig,
	PricingFilterValue,
	SearchQueryParams,
} from '@pages/VendorsSearch/containers/FilterPills/atoms';
import {
	getRangesByCategory,
	vanityFacetLabels,
	vanitySlugToFacetId,
} from '@pages/VendorsSearch/containers/FilterPills/components/StartingPriceFilter/utils';
import type {
	AppliedPricingFilterValue,
	FilterStrategy,
} from '@pages/VendorsSearch/containers/FilterPills/strategies/filter-strategy';

const pricingFilterNumberFormat = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
	minimumFractionDigits: 0,
	maximumFractionDigits: 0,
});

export class PricingFilterStrategy implements FilterStrategy {
	static readonly type = 'pricing';
	type = PricingFilterStrategy.type;
	readonly data: AppliedPricingFilterValue;
	readonly pricingRanges: number[];
	readonly categoryCode: string;
	constructor(
		filterValue: PricingFilterValue,
		config: FilterConfig,
		categoryCode: string,
	) {
		if (
			config.type !== PricingFilterStrategy.type ||
			!(filterValue.minPrice || filterValue.maxPrice)
		) {
			throw new Error('Invalid AppliedPricingFilterValue data');
		}
		this.data = {
			value: filterValue,
			config,
		};
		this.categoryCode = categoryCode;
		this.pricingRanges = getRangesByCategory(categoryCode);
	}

	calculateCount(): number {
		return 1;
	}

	getData() {
		return this.data;
	}

	getAppliedFilterPills(dynamicMaxRange?: number) {
		const [minRange, staticMaxRange] = this.pricingRanges;
		const maxRange = dynamicMaxRange ?? staticMaxRange;
		const { minPrice, maxPrice, vanitySlug } = this.data.value;
		let name = '';
		const trimmedVanitySlug = vanitySlug?.trim();

		// If vanitySlug is present, use the vanity label (e.g., from /affordable path)
		if (trimmedVanitySlug) {
			const categoryPrefixedKey = `${this.categoryCode}-${trimmedVanitySlug}`;
			name =
				vanityFacetLabels[categoryPrefixedKey] ||
				vanityFacetLabels[trimmedVanitySlug] ||
				trimmedVanitySlug;
		} else {
			if (
				minPrice !== undefined &&
				maxPrice !== undefined &&
				minPrice > minRange &&
				(dynamicMaxRange !== undefined || maxPrice < maxRange)
			) {
				name = `${pricingFilterNumberFormat.format(minPrice)} - ${pricingFilterNumberFormat.format(maxPrice)}`;
			} else if (minPrice !== undefined && minPrice > minRange) {
				name = `${pricingFilterNumberFormat.format(minPrice)}+`;
			} else if (
				maxPrice !== undefined &&
				(dynamicMaxRange !== undefined || maxPrice < maxRange)
			) {
				name = `Up to ${pricingFilterNumberFormat.format(maxPrice)}`;
			}
		}

		return [
			{
				filterId: this.data.config.id,
				name,
			},
		];
	}

	getAppliedFacetIds() {
		// Pricing filters don't have traditional facet IDs for UI/analytics purposes
		return [];
	}

	getSeoFacetIds() {
		// Return facet ID for vanity slugs so seoFilter() can match them
		const { vanitySlug } = this.data.value;
		if (!vanitySlug) {
			return [];
		}

		// Try category-prefixed key first (e.g., "REC-affordable")
		const categoryPrefixedKey = `${this.categoryCode}-${vanitySlug.trim()}`;
		const facetId = vanitySlugToFacetId[categoryPrefixedKey];

		return facetId ? [facetId] : [];
	}

	getAppliedFacetNames() {
		return [];
	}

	getAppliedFacetValues() {
		return [];
	}

	getSearchQueryParams(dynamicMaxRange?: number): Partial<SearchQueryParams> {
		const [minRange, staticMaxRange] = this.pricingRanges;
		const maxRange = dynamicMaxRange ?? staticMaxRange;
		const { minPrice, maxPrice } = this.data.value;
		if (!minPrice && !maxPrice) {
			return {};
		}
		return {
			rangeFilters: [
				{
					field: 'STARTING_PRICE',
					...(minPrice !== undefined &&
						minPrice > minRange && { gte: minPrice }),
					...(maxPrice !== undefined &&
						(dynamicMaxRange !== undefined || maxPrice < maxRange) && {
							lte: maxPrice,
						}),
				},
			],
		};
	}

	getUrlQueryParams(
		dynamicMaxRange?: number,
	): { key: string; value: string }[] {
		const [minRange, staticMaxRange] = this.pricingRanges;
		const maxRange = dynamicMaxRange ?? staticMaxRange;
		const { minPrice, maxPrice } = this.data.value;
		const params: { key: string; value: string }[] = [];
		if (minPrice !== undefined && minPrice > minRange) {
			params.push({ key: 'minPrice', value: minPrice.toString() });
		}
		if (
			maxPrice !== undefined &&
			(dynamicMaxRange !== undefined || maxPrice < maxRange)
		) {
			params.push({ key: 'maxPrice', value: maxPrice.toString() });
		}
		return params;
	}

	getAppliedFiltersForTracking(): { [filterName: string]: string | string[] } {
		const { minPrice, maxPrice } = this.data.value;
		const trackingObject: { [filterName: string]: string } = {};
		if (minPrice) {
			trackingObject.starting_price_min = minPrice.toString();
		}
		if (maxPrice) {
			trackingObject.starting_price_max = maxPrice.toString();
		}
		return trackingObject;
	}
}
